/**
 * Design tokens — single source of truth for the whole frontend.
 *
 * These values feed BOTH the Ant Design `ConfigProvider` theme (via `antdTheme.ts`)
 * and the Tailwind `@theme` block in `index.css`. When the claude.ai/design visual
 * direction is locked, change the values HERE — the Tailwind mirror in `index.css`
 * must be kept in sync (it cannot import TS at build time).
 *
 * Faza 1: placeholder values approximating the current look. Real palette / type
 * scale / radius land after the mockup is approved (see plan, "Ochiq nuqtalar").
 */

/**
 * Brand + semantic color scales.
 *
 * Direction: "green-forward govtech" — the domain is urban greenery + GPS field
 * monitoring, and the brand mark is a green check (✓) over blue pillars. Evergreen
 * is the primary chrome; civic blue is freed up as a map/data color; sprout green
 * marks verified/online; amber is the single warm signal (offline/overdue).
 */
export const colors = {
	// Primary (brand evergreen). Base is what AntD uses as `colorPrimary`.
	primary: {
		light: "#e6f2ec",
		base: "#1f6f54",
		hover: "#2e8b6b",
		active: "#155843",
	},
	// Verified / online / "now" — the logo's green check.
	success: {
		light: "#eaf6ea",
		base: "#43a047",
	},
	// Single warm signal: offline device, overdue task.
	warning: {
		light: "#fef6e6",
		base: "#e8a317",
	},
	error: {
		light: "#fdecec",
		base: "#e5484d",
	},
	// Civic blue (from the logo pillars): info + staff layer on the map.
	info: {
		light: "#eaf0fd",
		base: "#3d6fe0",
	},
	// Neutral scale with a faint green undertone (surfaces, borders, text).
	neutral: {
		0: "#ffffff",
		50: "#f4f7f3",
		100: "#eef2ee",
		200: "#e4eae4",
		300: "#d2dad3",
		400: "#aab4ab",
		500: "#7d877e",
		600: "#586059",
		700: "#3d443e",
		800: "#262b27",
		900: "#16201c",
	},
	// Dark sider surface (navigation chrome) — evergreen-ink, carries the brand.
	sider: {
		bg: "#15241d",
		itemSelectedBg: "#43a04733",
		itemHoverBg: "#ffffff14",
		spine: "#43a047",
	},
} as const;

/** Text colors (ink with a faint green-slate undertone, over light surfaces). */
export const text = {
	primary: "rgba(22, 32, 28, 0.92)",
	secondary: "rgba(22, 32, 28, 0.62)",
	tertiary: "rgba(22, 32, 28, 0.42)",
	disabled: "rgba(22, 32, 28, 0.26)",
	inverse: "#ffffff",
} as const;

/**
 * Typography. Three roles:
 * - display: Space Grotesk — headings + large dashboard numbers.
 * - body (fontFamily): Inter — dense UI / tables (AntD uses this).
 * - mono: JetBrains Mono — GPS coordinates, device IDs, timestamps, plates.
 */
export const typography = {
	fontFamily: "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
	fontFamilyDisplay: "'Space Grotesk', Inter, system-ui, sans-serif",
	fontFamilyMono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
	fontSizeBase: 15,
	fontSizeSm: 13,
	fontSizeLg: 16,
	fontSizeXl: 20,
	fontSizeHeading: 24,
	lineHeightBase: 1.5,
} as const;

/** Spacing scale (px). */
export const spacing = {
	xs: 4,
	sm: 8,
	md: 16,
	lg: 24,
	xl: 32,
} as const;

/** Border radius scale (px). */
export const radius = {
	sm: 4,
	base: 6,
	lg: 8,
	xl: 12,
} as const;

/** Box shadows. */
export const shadow = {
	sm: "0 1px 2px 0 rgba(0, 0, 0, 0.06)",
	base: "0 2px 8px 0 rgba(0, 0, 0, 0.08)",
	lg: "0 4px 16px 0 rgba(0, 0, 0, 0.12)",
} as const;

/** Layout dimensions. */
export const layout = {
	siderWidth: 280,
	siderCollapsedWidth: 80,
	headerHeight: 64,
} as const;

/** z-index scale. */
export const zIndex = {
	dropdown: 1050,
	modal: 1000,
	mapPanel: 900,
} as const;

export const tokens = {
	colors,
	text,
	typography,
	spacing,
	radius,
	shadow,
	layout,
	zIndex,
} as const;

export type Tokens = typeof tokens;
